import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  EMAILISH_PROJECT_NAME_WARNING,
  PROJECT_TITLE_AUTOFILL_ATTRS,
  looksLikeEmailAddress,
} from '../../utils/projectTitleInput';
import './ProjectTitleInput.css';

const UNLOCK_MS = 80;

/**
 * readOnly until shortly after focus — Chrome / password managers skip
 * locked fields on the initial autofill pass. Unlock is delayed so
 * autoFocus does not immediately re-open the field to fill.
 */
export function useAutofillShield() {
  const [locked, setLocked] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  const unlockOnFocus = useCallback(() => {
    if (timerRef.current != null || !locked) return;
    timerRef.current = window.setTimeout(() => {
      setLocked(false);
      timerRef.current = null;
    }, UNLOCK_MS);
  }, [locked]);

  // A real keystroke means a writer is typing: open the field now so the
  // character is not dropped while the focus delay is still running.
  const unlockNow = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setLocked(false);
  }, []);

  return { readOnly: locked, unlockOnFocus, unlockNow };
}

/**
 * Text input for a new or edited project / series title.
 * Autofill-hardened; soft-warns if the value looks like an email.
 */
const ProjectTitleInput = React.forwardRef(function ProjectTitleInput({
  className,
  value,
  onChange,
  onFocus,
  onKeyDown,
  onBeforeInput,
  onPointerDown,
  showEmailWarning = true,
  warningClassName,
  ...rest
}, ref) {
  const { readOnly, unlockOnFocus, unlockNow } = useAutofillShield();
  const reactId = useId();
  const warnId = `${reactId}-email-warn`;
  const warn = showEmailWarning && looksLikeEmailAddress(value);

  const handleFocus = (e) => {
    unlockOnFocus();
    onFocus?.(e);
  };

  // Clear the DOM attribute synchronously: the state update re-renders too
  // late, so the browser would still treat this same keystroke as read-only.
  const handleKeyDown = (e) => {
    if (e.currentTarget) e.currentTarget.readOnly = false;
    unlockNow();
    onKeyDown?.(e);
  };

  const handleBeforeInput = (e) => {
    if (e.currentTarget) e.currentTarget.readOnly = false;
    unlockNow();
    onBeforeInput?.(e);
  };

  const handleChange = (e) => {
    if (e.currentTarget) e.currentTarget.readOnly = false;
    unlockNow();
    onChange?.(e);
  };

  const handlePointerDown = (e) => {
    if (e.currentTarget) e.currentTarget.readOnly = false;
    unlockNow();
    onPointerDown?.(e);
  };

  return (
    <>
      <input
        type="text"
        {...rest}
        {...PROJECT_TITLE_AUTOFILL_ATTRS}
        ref={ref}
        className={className}
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput}
        onPointerDown={handlePointerDown}
        aria-describedby={warn ? warnId : undefined}
      />
      {warn && (
        <p id={warnId} className={warningClassName || 'project-title-email-warn'} role="status">
          {EMAILISH_PROJECT_NAME_WARNING}
        </p>
      )}
    </>
  );
});

export default ProjectTitleInput;
